import { useMemo } from 'react';

import { useApi } from '../../../common/hooks/use-api.hook';
import { ApiMethod } from '../../../common/models/api-types.type';
import { DELETE_ALL_PHRASE } from './queue-monitor.types';
import type { QueueStats, QueueType } from './queue-monitor.types';

/** The super-admin queue endpoints (BE pms-product.controller.ts, "ADMIN QUEUE MONITORING"). */
const BASE = '/services/pms/products/admin';

/**
 * The six queue-admin calls the job queues page makes — same URLs, methods,
 * params and bodies as before the page was split, through the same
 * `sendProtectedRequest`. Stable across renders, so it can sit in effect deps.
 */
export const useQueueAdminApi = () => {
    const { sendProtectedRequest } = useApi();

    return useMemo(
        () => ({
            getStats: (queue: QueueType, hours: number) =>
                sendProtectedRequest(ApiMethod.GET, `${BASE}/queue-stats?queue=${queue}&hours=${hours}`) as Promise<QueueStats>,

            retryFailed: (batchId: string) =>
                sendProtectedRequest(ApiMethod.POST, `${BASE}/queue-retry-failed`, { batchId }),

            abortBatch: (batchId: string) => sendProtectedRequest(ApiMethod.POST, `${BASE}/batch-abort/${batchId}`),

            /** Completed and failed jobs older than `olderThanDays`. */
            cleanup: (queue: QueueType, olderThanDays: number) =>
                sendProtectedRequest(ApiMethod.DELETE, `${BASE}/queue-cleanup?olderThanDays=${olderThanDays}&queue=${queue}`),

            /** Every job in the queue, whatever its status: the backend refuses it without the phrase. */
            deleteAll: (queue: QueueType) =>
                sendProtectedRequest(
                    ApiMethod.DELETE,
                    `${BASE}/queue-cleanup?queue=${queue}&olderThanDays=0&confirm=${DELETE_ALL_PHRASE}`,
                ),

            getJob: (jobId: string, queue: QueueType) =>
                sendProtectedRequest(ApiMethod.GET, `${BASE}/job/${jobId}?queue=${queue}`),

            replayJob: (jobId: string, queue: QueueType) =>
                sendProtectedRequest(ApiMethod.POST, `${BASE}/job/${jobId}/replay?queue=${queue}`),
        }),
        [sendProtectedRequest],
    );
};

export type QueueAdminApi = ReturnType<typeof useQueueAdminApi>;
